import { useEffect, useState } from 'react';
import { getOAuthDataConnect } from '../config/firebase';
import { getOAuthAuthorizeSessionPublic } from '../generated/dataconnect/oauth-user';
import type { OAuthAuthorizeSessionPublic } from '../types/oauthSession';

function normalizeSession(raw: {
  id: string;
  expiresAt: string;
  scope?: string[] | string | null;
  partner: {
    displayName: string;
    logoUrl?: string | null;
    iconUrl?: string | null;
    brandColor?: string | null;
    privacyPolicyUrl?: string | null;
  };
}): OAuthAuthorizeSessionPublic {
  let scope: string[] = [];
  if (Array.isArray(raw.scope)) {
    scope = raw.scope;
  } else if (typeof raw.scope === 'string') {
    scope = raw.scope.split(' ').filter(Boolean);
  }

  return {
    id: raw.id,
    expiresAt: raw.expiresAt,
    scope,
    partner: {
      displayName: raw.partner.displayName,
      logoUrl: raw.partner.logoUrl,
      iconUrl: raw.partner.iconUrl,
      brandColor: raw.partner.brandColor,
      privacyPolicyUrl: raw.partner.privacyPolicyUrl,
    },
  };
}

export function useOAuthAuthorizeSessionPublic(sessionId: string | null) {
  const [session, setSession] = useState<OAuthAuthorizeSessionPublic | null>(null);
  const [loading, setLoading] = useState(Boolean(sessionId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const dc = getOAuthDataConnect();
        const result = await getOAuthAuthorizeSessionPublic(dc, { sessionId });
        const row = result.data?.oauthAuthorizeSession;
        if (!row) {
          throw new Error('Authorization session not found or expired.');
        }
        if (!cancelled) {
          setSession(normalizeSession(row));
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load partner details.';
          setError(message);
          setSession(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { session, loading, error };
}
